<template>
  <div class="min-h-initial max-h-initial overflow-hidden text-gray-800">
    <div
      class="w-full h-full flex flex-col px-[14px] py-[22px] tablet-h:py-[30px] relative overflow-auto"
      :style="componentStyles"
    >
      <div class="w-full max-w-[1342px] mx-auto">
        <div class="mb-[26px]">
          <NewLink type="breadcrumb" @click="router.push('/infringement')">
            Comparison analyses
          </NewLink>
        </div>

        <div
          class="flex items-center justify-between mb-[26px] tablet-h:mb-[30px]"
        >
          <div>
            <div
              class="font-primary font-medium text-[22px] leading-[18px] mb-[13px] tablet-h:mb-2"
            >
              Technical comparison analysis report
            </div>

            <div class="font-secondary text-[14px] leading-[18px]">
              Flagged {{ formatTimestamp(infringement?.timestamp) }}
            </div>
          </div>
        </div>
      </div>

      <!-- Blurred report placeholder -->
      <div class="w-full max-w-[1342px] mx-auto relative">
        <div class="blur-sm pointer-events-none select-none opacity-75">
          <Card.Base class="mb-[60px]">
            <Card.Title icon="Info" class="mb-[25px]">
              Technical analysis overview
            </Card.Title>

            <Card.Content>
              <div
                class="grid grid-cols-1 mobile-l:grid-cols-2 tablet-h:grid-cols-3 gap-x-5 gap-y-[25px]"
              >
                <div class="bg-gray-200/20 rounded-xl p-5 pt-[14px]">
                  <div
                    class="font-secondary font-medium text-[14px] leading-[22px] text-gray-900 mb-[18px]"
                  >
                    Global match percentage
                  </div>

                  <div
                    class="size-[120px] tablet-h:size-[170px] relative flex items-center justify-center mx-auto rounded-full border-16 tablet-h:border-20 border-[#D8D8D8]"
                  >
                    <svg
                      class="size-[120px] tablet-h:size-[170px] absolute -rotate-90"
                      :class="scoreColorClass"
                    >
                      <circle
                        class="fill-transparent stroke-16 tablet-h:stroke-20"
                        :r="actualRadius"
                        :cx="radius"
                        :cy="radius"
                        :stroke-dasharray="`${circumference} ${circumference}`"
                        :stroke-dashoffset="strokeDashoffset"
                      />
                    </svg>

                    <div
                      class="font-primary font-medium text-center"
                      :class="scoreColorClass"
                    >
                      <div
                        class="text-[22px] tablet-h:text-[34px] leading-[28px] tablet-h:leading-[50px]"
                      >
                        {{ infringement?.matches?.score }}%
                      </div>
                      <div
                        class="text-[11px] tablet-h:text-[18px] leading-[13px] tablet-h:leading-[20px]"
                      >
                        match
                      </div>
                    </div>
                  </div>
                </div>

                <div class="bg-gray-200/20 rounded-xl p-5 pt-[14px]">
                  <div
                    class="font-secondary font-medium text-[14px] leading-[22px] text-gray-900 mb-[5px] tablet-h:mb-2"
                  >
                    Potential infringements
                  </div>

                  <div
                    class="font-primary text-[40px] leading-[40px] tablet-h:leading-[50px] font-medium text-gray-900 mb-2"
                  >
                    {{ infringement?.matches?.breakdown.length }}
                    <span class="hidden tablet-h:inline-block">matches</span>
                  </div>

                  <div class="flex flex-col gap-y-[3px]">
                    <div
                      v-for="(group, type) in groupedBreakdown"
                      :key="type"
                      class="flex items-center gap-x-2"
                    >
                      <Icon
                        :name="categoryIcons[type as MatchCategoryType]"
                        :size="16"
                        class="hidden tablet-h:flex stroke-gray-800"
                      />
                      <div
                        class="font-secondary text-[14px] leading-[22px] text-link"
                      >
                        {{ group.length }}
                        {{ (type as string).toLowerCase() }} matches
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  class="col-span-full tablet-h:col-span-1 bg-gray-200/20 rounded-xl p-5 pt-[14px]"
                >
                  <div
                    class="font-secondary font-medium text-[14px] leading-[22px] text-gray-900 mb-[5px] tablet-h:mb-2"
                  >
                    Comparing
                  </div>
                  <div class="flex flex-col gap-y-1">
                    <div class="flex items-center gap-x-2.5">
                      <ThumbnailIcon
                        class="min-w-[70px] tablet-h:min-w-[80px] max-w-[70px] tablet-h:max-w-[80px] h-[70px] tablet-h:h-20 bg-gray-200/25 rounded-[3px] p-[7px]"
                        :icon-size="iconSize"
                        :type="infringement?.copyrights[0]?.type"
                        :image-url="
                          infringement?.copyrights[0]?.asset?.previewImageUrl
                        "
                      />
                      <div class="w-full flex flex-col gap-y-1">
                        <div
                          class="font-secondary text-[12px] leading-[16px] text-gray-500"
                        >
                          Your registered work
                        </div>
                        <div class="font-secondary text-[14px] leading-[18px]">
                          {{ infringement?.copyrights[0]?.name }}
                        </div>
                        <div
                          class="font-primary text-[14px] leading-[22px] text-link"
                        >
                          View registration
                        </div>
                      </div>
                    </div>

                    <div class="w-[70px] tablet-h:w-20 flex justify-center">
                      <Icon name="Sort" :size="18" class="stroke-gray-800" />
                    </div>

                    <div class="flex items-center gap-x-2.5">
                      <ThumbnailIcon
                        class="min-w-[70px] tablet-h:min-w-[80px] max-w-[70px] tablet-h:max-w-[80px] h-[70px] tablet-h:h-20 bg-gray-200/25 rounded-[3px] p-[7px]"
                        :icon-size="iconSize"
                        :type="infringement?.infringingFile?.type"
                        :image-url="
                          infringement?.infringingFile?.asset?.previewImageUrl
                        "
                      />
                      <div class="w-full flex flex-col gap-y-1">
                        <div
                          class="font-secondary text-[12px] leading-[16px] text-gray-500"
                        >
                          Potential infringing work
                        </div>
                        <div class="font-secondary text-[14px] leading-[18px]">
                          {{ infringement?.infringingFile?.name }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <Card.Separator />

              <div
                class="font-secondary font-medium text-[14px] leading-[22px] mb-[15px]"
              >
                Infringment findings
              </div>
              <div class="font-secondary text-[14px] leading-[20px]">
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do
                eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut
                enim ad minim veniam, quis nostrud exercitation ullamco laboris
                nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor
                in reprehenderit in voluptate velit esse cillum dolore eu fugiat
                nulla pariatur. Excepteur sint occaecat cupidatat non proident,
                sunt in culpa qui officia deserunt mollit anim id est laborum.
                Sed ut perspiciatis unde omnis iste natus error sit voluptatem
                accusantium doloremque laudantium, totam rem aperiam, eaque ipsa
                quae ab illo inventore veritatis et quasi architecto beatae
                vitae dicta sunt explicabo.
              </div>
            </Card.Content>
          </Card.Base>
        </div>

        <!-- Overlay -->
        <div class="absolute inset-0 flex items-start justify-center pt-[40px]">
          <div
            class="w-full max-w-[600px] bg-white rounded-xl border border-gray-500/30 p-[30px]"
          >
            <template v-if="queryLoading">
              <Skeleton class="w-[280px] h-[24px] mb-[14px]" />
              <Skeleton class="w-full h-[68px] mb-[14px]" />
              <Skeleton class="w-[300px] h-[18px] mb-[14px]" />
              <div class="mb-[14px]">
                <div class="h-[42px] flex items-center gap-x-2.5">
                  <Skeleton class="min-w-[28px] h-[28px]" />
                  <Skeleton class="w-[160px] h-[14px]" />
                </div>
                <div class="h-[42px] flex items-center gap-x-2.5">
                  <Skeleton class="min-w-[28px] h-[28px]" />
                  <Skeleton class="w-[140px] h-[14px]" />
                </div>
              </div>
              <Skeleton class="w-[60px] h-[18px] mb-2" />
              <div
                v-for="n in 3"
                :key="n"
                class="h-[39px] flex items-center justify-between"
              >
                <Skeleton class="w-[120px] h-[14px]" />
                <Skeleton class="w-[130px] h-[14px]" />
              </div>
              <Skeleton class="w-full h-[40px] mt-[30px]" />
            </template>

            <template v-else>
              <div
                class="font-primary font-semibold text-[17px] leading-[24px] text-gray-800 mb-[14px]"
              >
                {{ infringement?.matches?.breakdown.length }} potential
                infringement matches
              </div>

              <div
                class="rounded-lg px-5 py-[14px] flex items-center justify-between mb-[14px]"
                :class="overallBgClass"
              >
                <div
                  class="max-w-[178px] text-[14px] leading-[20px] font-medium font-primary text-white"
                >
                  Overall potential infringement match
                </div>

                <div class="flex items-center">
                  <div
                    class="mr-2.5 font-secondary font-medium text-[16px] leading-[18px] text-white"
                  >
                    {{ infringement?.matches?.score }}%
                  </div>
                  <div class="w-[200px] h-2 bg-white rounded-md">
                    <div
                      class="h-full rounded-md border border-white"
                      :class="overallBarClass"
                      :style="{ width: `${infringement?.matches?.score}%` }"
                    ></div>
                  </div>
                </div>
              </div>

              <div
                class="font-secondary text-[14px] leading-[22px] text-gray-800 mb-[14px]"
              >
                Preview of potential infringement matches from report
              </div>

              <!-- File comparison rows -->
              <div class="mb-[14px]">
                <div
                  v-if="infringement?.copyrights[0]"
                  class="flex items-center justify-between"
                >
                  <div class="flex items-center gap-x-2.5">
                    <div
                      class="min-w-[28px] max-w-[28px] h-[28px] rounded bg-gray-200/30 flex items-center justify-center"
                    >
                      <ThumbnailIcon
                        class="w-[26px] h-[26px]"
                        :icon-size="26"
                        :type="infringement.copyrights[0].type"
                        :image-url="
                          infringement.copyrights[0].asset?.previewImageUrl
                        "
                      />
                    </div>
                    <div
                      class="font-secondary text-[14px] leading-[18px] text-gray-800"
                    >
                      {{ infringement.copyrights[0].name }}
                    </div>
                  </div>
                  <div
                    class="font-primary font-semibold text-[14px] leading-[18px] text-link"
                  >
                    View
                  </div>
                </div>

                <div
                  class="shdw min-w-5 w-5 h-5 bg-white rounded-full flex items-center justify-center border border-black/8 -my-1 ml-1 relative z-10"
                >
                  <Icon
                    id="scaling-stroke"
                    name="Sort"
                    :size="12"
                    class="stroke-gray-800"
                  />
                </div>

                <div
                  v-if="infringement?.infringingFile"
                  class="flex items-center justify-between"
                >
                  <div class="flex items-center gap-x-2.5">
                    <div
                      class="min-w-[28px] max-w-[28px] h-[28px] rounded bg-gray-200/30 flex items-center justify-center"
                    >
                      <ThumbnailIcon
                        class="w-[26px] h-[26px]"
                        :icon-size="26"
                        :type="infringement.infringingFile.type"
                        :image-url="
                          infringement.infringingFile.asset?.previewImageUrl
                        "
                      />
                    </div>
                    <div
                      class="font-secondary text-[14px] leading-[18px] text-gray-800"
                    >
                      {{ infringement.infringingFile.name }}
                    </div>
                  </div>
                  <div
                    class="font-primary font-semibold text-[14px] leading-[18px] text-link"
                  >
                    View
                  </div>
                </div>
              </div>

              <!-- Grouped breakdown -->
              <div
                v-for="(group, type) in groupedBreakdown"
                :key="type"
                class="mb-[14px] last:mb-0"
              >
                <div
                  class="h-[41px] flex items-center font-secondary font-medium text-[14px] leading-[18px] text-gray-800 border-b border-gray-800/8"
                >
                  {{ categoryLabels[type as MatchCategoryType] }}
                </div>

                <div
                  v-for="(item, index) in group.slice(0, 5)"
                  :key="index"
                  class="h-[39px] flex items-center justify-between border-b border-gray-800/8"
                >
                  <div class="flex items-center gap-x-2.5">
                    <Icon
                      :name="categoryIcons[type as MatchCategoryType]"
                      :size="16"
                      class="stroke-gray-800"
                    />
                    <div
                      class="font-secondary text-[14px] leading-[18px] text-gray-800"
                    >
                      {{ categoryLabels[type as MatchCategoryType] }} match
                      {{ index + 1 }}
                    </div>
                  </div>

                  <div class="flex items-center">
                    <div
                      class="mr-1.5 font-secondary font-medium text-[14px] leading-[18px]"
                      :class="scoreTextClass(item.level)"
                    >
                      {{ item.score }}%
                    </div>
                    <div class="w-[100px] h-2 bg-gray-200/50 rounded-md">
                      <div
                        class="h-full rounded-md"
                        :class="scoreBgClass(item.level)"
                        :style="{ width: `${item.score}%` }"
                      ></div>
                    </div>
                  </div>
                </div>

                <div
                  v-if="group.length > 5"
                  class="font-secondary text-[14px] leading-[18px] text-gray-500 py-2.5"
                >
                  + {{ group.length - 5 }} more
                </div>
              </div>

              <UiButton
                class="w-full mt-[30px]"
                primary
                :loading="loading"
                @click="handleClick"
              >
                Get report
              </UiButton>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import NewLink from '@/shared/components/Link.vue'
import { Dialogs } from '@/shared/dialogs/types'
import { useDialog } from '@/shared/dialogs/useDialog'
import { graphql } from '@/shared/graphql/router'
import {
  MatchCategoryType,
  ScoreLevel,
  type InfringementListQuery,
} from '@/shared/graphql/router/graphql'
import { useUserStore } from '@/shared/stores/user'
import { useDevice } from '@/shared/composables/useDevice'
import { useAvailableHeight } from '@/shared/composables/useAvailableHeight'
import { formatTimestamp } from '@/shared/utils/datetime'
import { Card, Icon, Skeleton, ThumbnailIcon, UiButton } from '@dacr/ui'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

type InfringementNode =
  InfringementListQuery['infringements']['edges'][number]['node']
type BreakdownItem = NonNullable<
  InfringementNode['matches']
>['breakdown'][number]

const route = useRoute()
const router = useRouter()
const device = useDevice()
const { availableHeight } = useAvailableHeight()
const userStore = useUserStore()
const { open } = useDialog()

const { result, loading: queryLoading } = useQuery(
  graphql(`
    query InfringementPreview($id: ID!) {
      infringements(input: { first: 1 }) {
        totalReports
      }
      infringement(id: $id) {
        id
        label
        timestamp
        copyrights {
          id
          name
          asset {
            previewImageUrl
          }
          type
        }
        infringingFile {
          id
          name
          asset {
            previewImageUrl
          }
          type
        }
        matches {
          score
          level
          breakdown {
            level
            score
            type
          }
        }
      }
    }
  `),
  () => ({
    id: route.params.id as string,
  }),
  {
    clientId: 'router',
  }
)

const infringement = computed(() => result.value?.infringement)
const totalReports = computed(() => result.value?.infringements.totalReports)
const credits = computed(() => userStore.user?.reportCredits || 0)

const { mutate: createReport, loading } = useMutation(
  graphql(`
    mutation CreateReport($input: CreateReportInput!) {
      createReport(input: $input) {
        success
        error {
          code
          message
        }
      }
    }
  `),
  {
    clientId: 'router',
  }
)

const componentStyles = computed(() => ({
  height: `${availableHeight.value}px`,
}))

const groupedBreakdown = computed(() => {
  const groups: Partial<Record<MatchCategoryType, BreakdownItem[]>> = {}
  for (const item of infringement.value?.matches?.breakdown ?? []) {
    if (!groups[item.type]) {
      groups[item.type] = []
    }
    groups[item.type]!.push(item)
  }
  return groups
})

const radius = computed(() =>
  device.isDesktop.value || device.isTabletHorizontal.value ? 85 : 60
)

const actualRadius = computed(() =>
  device.isDesktop.value || device.isTabletHorizontal.value
    ? radius.value - 10
    : radius.value - 8
)

const circumference = computed(() => actualRadius.value * 2 * Math.PI)

const strokeDashoffset = computed(
  () =>
    circumference.value -
    ((infringement.value?.matches?.score || 0) / 100) * circumference.value
)

const scoreColorClass = computed(() => ({
  'stroke-red-500 text-red-500':
    infringement.value?.matches?.level === ScoreLevel.High,
  'stroke-yellow-500 text-yellow-500':
    infringement.value?.matches?.level === ScoreLevel.Medium,
  'stroke-green-500 text-green-500':
    infringement.value?.matches?.level === ScoreLevel.Low,
}))

const iconSize = computed(() =>
  device.isMobile.value || device.isTabletVertical.value ? 56 : 66
)

const overallBgClass = computed(() => ({
  'bg-green-500': infringement.value?.matches?.level === ScoreLevel.Low,
  'bg-yellow-500': infringement.value?.matches?.level === ScoreLevel.Medium,
  'bg-red-500': infringement.value?.matches?.level === ScoreLevel.High,
}))

const overallBarClass = computed(() => ({
  'bg-green-600': infringement.value?.matches?.level === ScoreLevel.Low,
  'bg-yellow-600': infringement.value?.matches?.level === ScoreLevel.Medium,
  'bg-red-500': infringement.value?.matches?.level === ScoreLevel.High,
}))

const categoryLabels: Record<MatchCategoryType, string> = {
  [MatchCategoryType.Audio]: 'Audio',
  [MatchCategoryType.Image]: 'Visual',
  [MatchCategoryType.Text]: 'Text',
  [MatchCategoryType.Transcript]: 'Transcript',
  [MatchCategoryType.Video]: 'Video',
}

const categoryIcons: Record<MatchCategoryType, string> = {
  [MatchCategoryType.Audio]: 'Audio',
  [MatchCategoryType.Image]: 'Image',
  [MatchCategoryType.Text]: 'FileText',
  [MatchCategoryType.Transcript]: 'Text',
  [MatchCategoryType.Video]: 'Play',
}

const scoreTextClass = (level: ScoreLevel) => ({
  'text-green-500': level === ScoreLevel.Low,
  'text-yellow-500': level === ScoreLevel.Medium,
  'text-red-500': level === ScoreLevel.High,
})

const scoreBgClass = (level: ScoreLevel) => ({
  'bg-green-500': level === ScoreLevel.Low,
  'bg-yellow-500': level === ScoreLevel.Medium,
  'bg-red-500': level === ScoreLevel.High,
})

const handleCreateReport = async () => {
  if (!infringement.value) return false

  const res = await createReport({
    input: {
      copyrightId: infringement.value.copyrights[0].id,
      comparingId: infringement.value.infringingFile.id,
    },
  })

  if (res?.data?.createReport.success) {
    userStore.updateUser({ isCreatingReport: true })
    router.push('/infringement')
    return true
  }

  return false
}

const handleIntroGetReport = () => {
  if (!credits.value) {
    return open(Dialogs.BuyReportCredits, {
      props: {
        onSuccess: handleCreateReport,
      },
    })
  }

  handleCreateReport()
}

const handleClick = () => {
  if (loading.value) return

  if (!totalReports.value) {
    return open(Dialogs.InfringementReport, {
      events: {
        success: handleIntroGetReport,
      },
    })
  }

  if (!credits.value) {
    return open(Dialogs.BuyReportCredits, {
      props: {
        onSuccess: handleCreateReport,
      },
    })
  }

  handleCreateReport()
}
</script>
